<template>
  <div class="history-section">
    <div class="history-section-header">
      <div class="text-h2">
        Acknowledgement History
      </div>
      <HideRbac :permissions="['downloadJobProfileAcknowledgementPdf']">
        <DpplDarkTooltip
          tooltip-placement="left"
          light-mode
        >
          <template #activator="{ onMouseenter, onMouseleave }">
            <span
              class="download-btn-tooltip-wrapper"
              @mouseenter="onMouseenter"
              @mouseleave="onMouseleave"
            >
              <v-btn
                class="history-download-btn"
                :class="{ 'history-download-btn-disabled': !canDownloadAllPDFs }"
                variant="outlined"
                size="small"
                icon
                :disabled="!canDownloadAllPDFs"
                @click="downloadAllPDFs"
              >
                <v-icon icon="$tray-arrow-down" />
              </v-btn>
            </span>
          </template>
          <template #tooltip-content>
            <p class="text-body-2 text-textDark">
              {{ downloadAllTooltipText }}
            </p>
          </template>
        </DpplDarkTooltip>
      </HideRbac>
    </div>
    <EmployeeAcknowledgementHistoryEmptyState
      v-if="!acknowledgements.length"
    />
    <div
      v-else
      class="history-table-wrap"
    >
      <table class="history-table text-body-2">
        <thead>
          <tr>
            <th class="text-left">
              <span class="history-th-content">
                Job Profile
                <InsightsTooltip
                  :tooltip="sliderHeaderProfileTooltip"
                  tooltip-direction="end"
                  class="ml-1"
                  @click.stop
                />
              </span>
            </th>
            <th class="text-left created-col">
              <span class="history-th-content">
                Created
                <InsightsTooltip
                  :tooltip="sliderHeaderCreatedDateTooltip"
                  tooltip-direction="end"
                  class="ml-1"
                  @click.stop
                />
              </span>
            </th>
            <th class="text-left">
              <span class="history-th-content">
                Status
                <InsightsTooltip
                  :tooltip="sliderHeaderStatusTooltip"
                  tooltip-direction="end"
                  class="ml-1"
                  @click.stop
                />
              </span>
            </th>
            <th
              class="text-right"
              style="width: 48px"
            />
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="ack in acknowledgements"
            :key="ack.ulid"
          >
            <td>
              <div class="history-job-profile-cell">
                <div class="text-body-2 text-textDark">
                  {{ ack.title }}
                </div>
                <div
                  v-if="ack.hris_job_code"
                  class="text-body-3 text-textLight"
                >
                  {{ ack.hris_job_code }}
                </div>
              </div>
            </td>
            <td class="text-textDark created-cell">
              <div>{{ formatDisplayDate(ack.created_at) }}</div>
              <div
                v-if="ack.created_by?.name"
                class="text-textLight"
              >
                by {{ ack.created_by.name }}
              </div>
            </td>
            <td class="status-cell">
              <DpplDarkTooltip
                v-if="ack.status === 'cancelled'"
                tooltip-placement="top"
                :tooltip-offset="12"
                light-mode
              >
                <template #activator="{ onMouseenter, onMouseleave }">
                  <span
                    class="cancelled-status-pill"
                    @mouseenter="onMouseenter"
                    @mouseleave="onMouseleave"
                  >
                    <StatusPill
                      :status-key="acknowledgementStatusKey(ack.status)"
                      :label="acknowledgementStatusLabel(ack.status)"
                    />
                  </span>
                </template>
                <template #tooltip-content>
                  <div class="text-body-2 text-textDark">
                    <div>{{ formatDisplayDate(ack.cancelled_at) }}</div>
                    <div v-if="ack.cancelled_by?.name" class="text-textLight">
                      by {{ ack.cancelled_by.name }}
                    </div>
                  </div>
                </template>
              </DpplDarkTooltip>
              <StatusPill
                v-else
                :status-key="acknowledgementStatusKey(ack.status)"
                :label="acknowledgementStatusLabel(ack.status)"
              />
            </td>
            <td class="text-right">
              <v-menu
                v-if="ack.status === 'signed' || ack.status === 'pending'"
                class="history-ack-menu"
                content-class="history-ack-dropdown"
                location="bottom end"
                transition="scale-transition"
              >
                <template #activator="{ props: menuProps }">
                  <v-btn
                    v-bind="menuProps"
                    icon
                    variant="text"
                    size="small"
                    color="textDark"
                  >
                    <v-icon icon="$dots-vertical" />
                  </v-btn>
                </template>
                <v-list
                  density="compact"
                  class="history-ack-list"
                >
                  <template v-if="ack.status === 'signed'">
                    <v-list-item
                      v-if="checkRbacPermissions(['viewJobProfileAcknowledgementPdf'])"
                      :href="getEmployeeAcknowledgementViewPdfUrl(ack.ulid)"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="history-ack-list-item text-body-2 text-textDark"
                    >
                      <template #prepend>
                        <v-icon
                          icon="$magnify"
                          size="small"
                        />
                      </template>
                      <v-list-item-title>View</v-list-item-title>
                    </v-list-item>
                    <v-list-item
                      v-if="checkRbacPermissions(['downloadJobProfileAcknowledgementPdf'])"
                      class="history-ack-list-item text-body-2 text-textDark"
                      @click="downloadAcknowledgementPdf(ack.ulid)"
                    >
                      <template #prepend>
                        <v-icon
                          icon="$tray-arrow-down"
                          size="small"
                        />
                      </template>
                      <v-list-item-title>Download</v-list-item-title>
                    </v-list-item>
                  </template>
                  <v-list-item
                    v-else-if="ack.status === 'pending'
                      && checkRbacPermissions(['cancelEmployeeJobProfileAcknowledgements'])"
                    class="history-ack-list-item history-ack-list-item-cancel text-body-2"
                    @click="handleHistoryCancelClick(ack)"
                  >
                    <template #prepend>
                      <v-icon
                        icon="$delete-outline"
                        size="small"
                        color="error"
                      />
                    </template>
                    <v-list-item-title>Cancel</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import EmployeeAcknowledgementHistoryEmptyState
  from '@/components/library/employeeAcknowledgements/EmployeeAcknowledgementHistoryEmptyState.vue';
import StatusPill from '@/components/shared/StatusPill.vue';
import DpplDarkTooltip from '@/components/datapeople/DpplDarkTooltip.vue';
import HideRbac from '@/components/rbac/HideRbac.vue';
import InsightsTooltip from '@/components/insights/InsightsTooltip.vue';
import { getTooltip } from '@/helpers/tooltips';
import { formatDisplayDate } from '@/helpers/date';
import {
  getEmployeeAcknowledgementViewPdfUrl,
  getEmployeeAcknowledgementDownloadPdfUrl,
  getEmployeeAcknowledgementDownloadZipUrl,
} from '@/api/jobProfile';
import { clickToDownload } from '@/helpers/fileDownload';
import { useAppAuthData } from '@/stores/app/authData';
import { useAppSnackbar } from '@/stores/app/snackbar';
import { useTracking } from '@/stores/tracking/tracking';
import {
  acknowledgementStatusKey,
  acknowledgementStatusLabel,
} from '@/components/library/employeeAcknowledgements/helpers';

const MAX_SIGNED_FOR_DOWNLOAD_ALL = 15;

const { addSuccessSnackbar, addErrorSnackbar } = useAppSnackbar();

const props = defineProps({
  acknowledgements: {
    type: Array,
    default: () => [],
  },
});

const hasSignedAcknowledgements = computed(() => (
  props.acknowledgements.some((ack) => ack.status === 'signed')
));

const signedAcknowledgementsCount = computed(() => (
  props.acknowledgements.filter((ack) => ack.status === 'signed').length
));

const canDownloadAllPDFs = computed(() => (
  hasSignedAcknowledgements.value && signedAcknowledgementsCount.value <= MAX_SIGNED_FOR_DOWNLOAD_ALL
));

const downloadAllTooltipText = computed(() => {
  if (!hasSignedAcknowledgements.value) {
    return 'No signed acknowledgement PDFs available to download.';
  }

  if (signedAcknowledgementsCount.value > MAX_SIGNED_FOR_DOWNLOAD_ALL) {
    return 'Too many signed PDFs to download at once (max 15). '
      + 'Download them one by one from the list below.';
  }

  return 'Download All PDFs';
});

const { checkRbacPermissions } = useAppAuthData();

const sliderHeaderProfileTooltip = computed(() => getTooltip('acknowledgement-slider-header-profile'));
const sliderHeaderCreatedDateTooltip = computed(() => getTooltip('acknowledgement-slider-header-created_date'));
const sliderHeaderStatusTooltip = computed(() => getTooltip('acknowledgement-slider-header-status'));

const emit = defineEmits(['cancel']);

const handleHistoryCancelClick = (ack) => {
  useTracking().ackLibClickedCancel({ location: 'In History Table' });

  emit('cancel', ack);
};

const downloadAcknowledgementPdf = (ulid) => {
  try {
    const pdfUrl = getEmployeeAcknowledgementDownloadPdfUrl(ulid);

    clickToDownload(pdfUrl);

    useTracking().ackLibDownloadedPdf({ type: 'Individual from History Table' });

    addSuccessSnackbar({ text: 'Your PDF is downloading.' });
  } catch (error) {
    addErrorSnackbar({
      text: error?.message || 'Failed to start PDF download.',
    });
  }
};

const downloadAllPDFs = () => {
  const tooManyToDownloadSnackbarMessage = `Too many to download at once. 
    Please download them one by one from the list below.`;

  const signedAcknowledgements = props.acknowledgements.filter((ack) => ack.status === 'signed');

  if (!signedAcknowledgements.length) return;

  if (signedAcknowledgements.length > MAX_SIGNED_FOR_DOWNLOAD_ALL) {
    addErrorSnackbar({ text: tooManyToDownloadSnackbarMessage });

    return;
  }

  const count = signedAcknowledgements.length;

  try {
    if (count === 1) {
      const pdfUrl = getEmployeeAcknowledgementDownloadPdfUrl(signedAcknowledgements[0].ulid);

      clickToDownload(pdfUrl);

      useTracking().ackLibDownloadedPdf({ type: 'All' });

      addSuccessSnackbar({ text: 'Your PDF is downloading.' });
    } else {
      const ulids = signedAcknowledgements.map((ack) => ack.ulid);
      const zipUrl = getEmployeeAcknowledgementDownloadZipUrl(ulids);

      clickToDownload(zipUrl);

      useTracking().ackLibDownloadedPdf({ type: 'All' });

      addSuccessSnackbar({
        text: `Your ZIP file with ${count} PDFs is downloading.`,
      });
    }
  } catch (error) {
    addErrorSnackbar({
      text: error?.message || 'Failed to start PDF download.',
    });
    throw error;
  }
};

</script>

<style scoped lang="less">
.history-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 0 24px 16px 24px;
}

.history-section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.history-download-btn {
  min-width: 36px;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  border: 1px solid @stroke-default;
  color: @text-dark;
  background-color: @bg-default;
}

.history-download-btn-disabled {
  background-color: @grey-200 !important;
}

.download-btn-tooltip-wrapper {
  display: inline-block;
}

.history-table-wrap {
  overflow-x: auto;
  margin-left: -24px;
  margin-right: -24px;
  padding-left: 5px;
  padding-right: 5px;
}

.history-table {
  width: 100%;
  border-collapse: collapse;

  th {
    vertical-align: middle;
  }

  tbody td {
    vertical-align: top;
  }

  th {
    padding: 18px 16px;
    font-weight: 600;
    color: @text-darkest;
    background-color: @grey-300;
    border-bottom: 1px solid @stroke-default;
  }

  .created-col,
  .created-cell {
    max-width: 170px;
    overflow-wrap: break-word;
  }

  .history-th-content {
    display: inline-flex;
    align-items: center;
  }

  td {
    padding: 12px;
    border-bottom: 1px solid @stroke-default;
  }

  tbody tr:last-child td {
    border-bottom: none;
  }

  .history-job-profile-cell {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .status-cell :deep(.ack-status-pill) {
    min-width: 86px;
  }
}
</style>

<style lang="less">
.history-ack-dropdown,
.history-ack-dropdown .v-list {
  box-shadow: none !important;
}

.history-ack-dropdown {
  border-radius: 8px !important;
  border: 1px solid @stroke-default !important;
  padding: 12px !important;
  background-color: @white !important;

  .history-ack-list {
    padding: 0 !important;
  }

  .history-ack-list-item.v-list-item {
    min-height: 40px;
    padding-inline: 0;
    padding-block: 0;

    .v-list-item__content {
      padding: 0;
    }

    .v-list-item__overlay {
      background: transparent;
    }

    .v-list-item__prepend {
      min-width: 0;
      max-width: 20px;
      margin-inline-end: 6px;
    }

    .v-list-item__prepend .v-icon {
      font-size: 18px;
    }

    .v-list-item-title {
      font-size: 13px;
      font-weight: 500;
      line-height: 20px;
    }
  }

  .history-ack-list-item:hover {
    cursor: pointer;
  }

  .history-ack-list-item:hover .v-list-item__overlay {
    background: @bg-canvas;
  }

  .history-ack-list-item:hover .v-list-item-title,
  .history-ack-list-item:hover .v-list-item__prepend .v-icon {
    color: @text-brand;
  }

  .history-ack-list-item-cancel:hover .v-list-item-title,
  .history-ack-list-item-cancel:hover .v-list-item__prepend .v-icon {
    color: @text-error;
  }
}
</style>
